import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import StageShell from "@/components/StageShell";
import ChangeCard from "@/components/ChangeCard";
import { useRun } from "@/hooks/useRun";
import { api, errMsg } from "@/lib/api";

const AGENTS = { ats: "ATS Screener", agency: "Agency Recruiter", inhouse: "In-house Recruiter", hm: "Hiring Manager", aitext: "AI-Text Detector", fact: "Fact Auditor" };
const tone = (n) => (n >= 8 ? "text-emerald-300 bg-emerald-500/10" : n >= 6 ? "text-amber-300 bg-amber-500/10" : "text-rose-300 bg-rose-500/10");

function Scoreboard({ rounds }) {
  return (
    <div className="card-surface p-0 overflow-hidden">
      <table className="w-full text-sm" data-testid="panel-scoreboard">
        <thead className="bg-slate-900/70 text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-2.5">Round</th>{Object.values(AGENTS).map((a) => <th key={a} className="px-2">{a}</th>)}</tr>
        </thead>
        <tbody>
          {rounds.map((r) => (
            <tr key={r.id} className="border-t border-border" data-testid={`panel-round-row-${r.round}`}>
              <td className="px-4 py-2 font-mono">R{r.round} <span className="text-xs text-slate-500">{r.status !== "done" && r.status}</span></td>
              {Object.keys(AGENTS).map((k) => {
                const a = r.agents?.[k];
                const final = r.scores?.[k] ?? a?.debate?.revised_score;
                return (
                  <td key={k} className="px-2 text-center">
                    {a ? <span className={`inline-block px-2 py-0.5 rounded font-mono ${tone(final ?? a.score)}`}>{a.score}{final != null && final !== a.score ? ` → ${final}` : ""}</span> : <span className="text-slate-600">…</span>}
                  </td>
                );
              })}
            </tr>
          ))}
          {!rounds.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No rounds yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AgentCards({ r }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {Object.entries(AGENTS).map(([k, name]) => {
        const a = r.agents?.[k];
        return (
          <div key={k} className="card-surface py-3" data-testid={`panel-agent-${k}`}>
            <div className="flex items-center"><div className="font-semibold text-sm">{name}</div>{a && <span className={`ml-auto px-2 rounded font-mono text-sm ${tone(a.debate?.revised_score ?? a.score)}`}>{a.debate?.revised_score ?? a.score}/10</span>}</div>
            {!a ? <div className="text-xs text-muted-foreground mt-2">Reviewing…</div> : (
              <>
                <ul className="list-disc pl-4 text-xs text-slate-300 mt-2 space-y-0.5">{a.top_issues.map((t, i) => <li key={i}>{t}</li>)}</ul>
                {a.interview_questions?.length > 0 && <div className="text-[11px] text-slate-500 mt-2">Questions raised: {a.interview_questions.join(" · ")}</div>}
                {a.debate && <div className="mt-2 space-y-1">{a.debate.responses.slice(0, 4).map((d, i) => (
                  <div key={i} className="text-[11px]"><span className={d.stance === "agree" ? "text-emerald-400" : "text-amber-400"}>{d.stance}</span> <span className="text-slate-500">{d.agent}:</span> <span className="text-slate-400">{d.point}</span></div>
                ))}</div>}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Stage6({ job, refresh }) {
  const [rounds, setRounds] = useState(null);
  const [maxRounds, setMaxRounds] = useState(3);
  useEffect(() => { api.get("/settings").then(({ data }) => setMaxRounds(data.max_panel_rounds)); }, []);
  const load = useCallback(() => api.get(`/jobs/${job.id}/panel`).then(({ data }) => setRounds(data)), [job.id]);
  const runHook = useRun(job.id, 6, () => { load(); refresh(); });
  const busy = runHook.busy;
  useEffect(() => { if (busy) refresh(); }, [busy, refresh]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!runHook.busy) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [runHook.busy, load]);
  if (rounds === null) return <div className="text-sm text-muted-foreground">Loading panel…</div>;
  const last = rounds[rounds.length - 1];
  const decide = async (ch, action, text) => {
    try { await api.post(`/panel/${last.id}/changes/${ch.id}`, { action, text }); load(); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const allPass = (last?.scores && Object.values(last.scores).every((s) => s >= 8)) || (last && last.round >= maxRounds);
  return (
    <StageShell job={job} stage={6} refresh={refresh} runHook={runHook}
      runs={[{ action: "panel_round", label: last ? (last.round >= maxRounds ? `Max ${maxRounds} rounds reached` : `Run round ${last.round + 1}`) : "Start panel round 1", testid: "panel-run-round-button", disabled: allPass }]}>
      <div className="text-sm text-muted-foreground">Six AI reviewers score the draft against 5 representative JDs, debate once, then a moderator ranks fixes and a writer applies them. Stops when everyone scores 8+ or after the max rounds set in Settings.</div>
      <Scoreboard rounds={rounds} />
      {last && (
        <>
          {last.jds?.length > 0 && <div className="text-xs text-slate-500">Representative JDs: {last.jds.join(" · ")}</div>}
          {last.keyword_match && (
            <div data-testid="panel-keyword-match" className={`text-sm rounded-lg border px-3 py-2 ${last.keyword_match.if_accepted >= last.keyword_match.target ? "border-emerald-500/40 text-emerald-200" : "border-amber-500/40 text-amber-200"}`}>
              Keyword match on skills the client has: {last.keyword_match.before}% now → {last.keyword_match.if_accepted}% if you accept this round's fixes (target {last.keyword_match.target}%).
            </div>
          )}
          <AgentCards r={last} />
          <div className="flex items-center gap-3"><div className="font-semibold">Round {last.round} fixes</div><span className="text-xs text-muted-foreground">{last.changes.filter((c) => c.status === "pending").length} pending</span></div>
          <div className="space-y-3" data-testid="panel-fix-list">
            {last.changes.map((ch) => {
              const fix = last.fixes.find((f) => f.id === ch.fix_id);
              return (
                <ChangeCard key={ch.id} ch={ch} onDecide={decide} idp="panel-fix">
                  {fix && <div className="text-xs text-slate-400"><span className="font-mono text-slate-500">#{fix.priority}</span> {fix.instruction} <span className="text-slate-600">({fix.raised_by.join(", ")})</span></div>}
                  {ch.warnings?.map((w) => <div key={w} className="text-xs text-rose-300 flex gap-1"><AlertTriangle size={12} />{w}</div>)}
                </ChangeCard>
              );
            })}
            {last.status === "done" && !last.changes.length && <div className="text-sm text-muted-foreground">No line changes proposed this round.</div>}
          </div>
        </>
      )}
    </StageShell>
  );
}
